import { useEffect, useMemo, useState } from 'react';
import { fetchHubSpotOwners, HubSpotOwner } from '../services/api';

/**
 * [CASE-ASSIGN 2026-08-13] The live HubSpot PM/Sales roster, shared by every
 * component that has to name a person: the notification bell's two assign
 * dropdowns and the Team / Sales-PM Name dropdowns in the Refunds and Disputes
 * tables. One definition of "who is a current PM or Sales person" so the bell and
 * the table can never offer different people.
 *
 * `/api/hubspot/owners` is served from the server's hourly roster cache, so a
 * HubSpot team change shows up here within the hour. The module-level cache below
 * collapses every consumer to ONE request per page load (the bell used to fetch
 * its own copy); a failed load clears the in-flight promise so the next mount
 * retries instead of caching the failure.
 */

let cache: HubSpotOwner[] | null = null;
let inflight: Promise<HubSpotOwner[]> | null = null;

function loadOwners(): Promise<HubSpotOwner[]> {
  if (cache) return Promise.resolve(cache);
  if (!inflight) {
    inflight = fetchHubSpotOwners()
      .then((rows) => { cache = rows; return rows; })
      .catch((err) => { inflight = null; throw err; });
  }
  return inflight;
}

export type CaseTeam = 'PM' | 'Sales';

export interface HubSpotRoster {
  owners: HubSpotOwner[];
  /** Every current PM + Sales person, alphabetical — the table's combined name dropdown. */
  names: string[];
  /** PM-side names only (role pm or both) — the bell's PM column. */
  pmNames: string[];
  /** Sales-side names only (role sales or both) — the bell's Sales column. */
  salesNames: string[];
  /**
   * Which Team a name implies. `null` when it can't be derived — either the person
   * sits in BOTH kinds of HubSpot team (Team is genuinely ambiguous, so leave whatever
   * the row already has) or the name isn't on the roster at all.
   */
  teamOf: (name: string) => CaseTeam | null;
  /**
   * May this name sit under that Team? A person in both kinds of team fits either
   * side; a name that isn't on the roster fits neither, so switching Team drops it.
   */
  fitsTeam: (name: string, team: CaseTeam) => boolean;
}

export function useHubSpotRoster(enabled = true): HubSpotRoster {
  const [owners, setOwners] = useState<HubSpotOwner[]>(cache ?? []);

  useEffect(() => {
    if (!enabled) return;
    let active = true;
    loadOwners()
      .then((rows) => { if (active) setOwners(rows); })
      .catch(() => { /* dropdowns fall back to the row's stored value */ });
    return () => { active = false; };
  }, [enabled]);

  return useMemo(() => {
    const collator = new Intl.Collator();
    const sortUnique = (rows: HubSpotOwner[]) =>
      Array.from(new Set(rows.map((o) => o.name).filter(Boolean))).sort(collator.compare);

    const assigned = owners.filter((o) => o.role !== null);
    // Normalised name → role, so a stored value matches regardless of casing/padding.
    const roleByName = new Map<string, NonNullable<HubSpotOwner['role']>>();
    for (const o of assigned) {
      if (o.role) roleByName.set(o.name.trim().toLowerCase(), o.role);
    }

    const roleOf = (name: string) => roleByName.get(String(name ?? '').trim().toLowerCase());

    return {
      owners,
      names: sortUnique(assigned),
      pmNames: sortUnique(owners.filter((o) => o.role === 'pm' || o.role === 'both')),
      salesNames: sortUnique(owners.filter((o) => o.role === 'sales' || o.role === 'both')),
      teamOf: (name: string) => {
        const role = roleOf(name);
        return role === 'pm' ? 'PM' : role === 'sales' ? 'Sales' : null;
      },
      fitsTeam: (name: string, team: CaseTeam) => {
        const role = roleOf(name);
        if (!role) return false;
        return role === 'both' || (team === 'PM' ? role === 'pm' : role === 'sales');
      },
    };
  }, [owners]);
}
